// Copyright © 2019-2026 Focal Systems. All rights reserved.

import type { ReactNode } from "react";

/** Light/dark pair of transparent images under `public/`, drawn in Focal green and tinted to the accent. */
export interface StatusPageIllustration {
  light: string;
  dark: string;
}

interface StatusPageProps {
  illustration: StatusPageIllustration;
  title: string;
  description: string;
  /** Optional supporting line under the description, e.g. the missing permission. */
  detail?: ReactNode;
  footer?: ReactNode;
}

/**
 * Full-page explanation for a page the user can't see — not found, access denied. Sits inside the app
 * shell under a `PageHeader` and fills the remaining height (give the parent `flex min-h-full flex-col`) to sit
 * vertically centred. The illustration follows the accent via `.accent-tint`.
 */
export function StatusPage({ illustration, title, description, detail, footer }: StatusPageProps) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-6 px-6 py-12 text-center">
      <img src={illustration.light} alt="" className="accent-tint h-auto w-full max-w-sm dark:hidden" />
      <img src={illustration.dark} alt="" className="accent-tint hidden h-auto w-full max-w-sm dark:block" />
      <div className="max-w-md space-y-2">
        <h2 className="text-xl font-semibold tracking-tight">{title}</h2>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      {detail}
      {footer && <div className="text-xs text-muted-foreground">{footer}</div>}
    </div>
  );
}

/** A monospace chip for the `detail` slot — e.g. the missing permission string. */
export function StatusPageChip({ children }: { children: ReactNode }) {
  return (
    <code className="max-w-full truncate rounded-md border bg-muted px-2 py-1 font-mono text-xs text-muted-foreground">
      {children}
    </code>
  );
}
